import React from 'react';
import PageWithAsyncData from '../components/PageWithAsyncData';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useAssessmentTest } from '../hooks/use-hydra/teams';
import Navbar from '../components/Navbar';
import { NAVBAR_HEIGHT } from '../components/Navbar';
import Step from '../components/AssessmentTest/Step';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { TestIntro } from '../components/AssessmentTest/TestIntro';
import TestOutro from '../components/AssessmentTest/TestOutro';
import {
  AssessmentTestContext,
  AssessmentTestProvider,
} from '../contexts/assessment-test';
import TestTimeout from '../components/AssessmentTest/TestTimeout';
import { useAutosave, ExitGuard } from '../hooks/assessment-test/use-autosave';
import { usePageLock } from '../hooks/assessment-test/use-page-lock';
import TestWarningMultipleTabs from '../components/AssessmentTest/TestWarningMultipleTabs';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  flex: 1;
  gap: 16px;
  overflow-y: auto;
`;

export default function AssessmentTest() {
  const { companySlug, testSlug } =
    useParams<{ companySlug: string; testSlug: string }>();
  const {
    loading: isTestLoading,
    error: testError,
    retry: refetchTestData,
    value: testData,
  } = useAssessmentTest(companySlug, testSlug);

  const animOptions = React.useRef({
    duration: 500,
    easing: 'ease-in-out',
  }).current;
  const [animRef] = useAutoAnimate(animOptions);

  return (
    <PageWithAsyncData
      isLoading={isTestLoading}
      error={testError}
      retry={refetchTestData}
    >
      <Navbar
        breadCrumbs={[
          {
            label: 'Assessment Test',
            url: null,
          },
        ]}
      />
      {testData && (
        <AssessmentTestProvider test={testData}>
          <Content ref={animRef}>
            <AssessmentTestContent />
          </Content>
        </AssessmentTestProvider>
      )}
    </PageWithAsyncData>
  );
}

function AssessmentTestContent() {
  const { testSlug } = useParams<{ testSlug: string }>();
  const [state] = React.useContext(AssessmentTestContext);
  useAutosave();
  const { isOpenInAnotherWindow } = usePageLock(testSlug);

  if (isOpenInAnotherWindow) {
    return <TestWarningMultipleTabs />;
  }

  return (
    <>
      {state.screen.type === 'INTRO' && <TestIntro key={`test-intro`} />}
      {state.screen.type === 'OUTRO' && <TestOutro key={`test-outro`} />}
      {state.screen.type === 'TIMEOUT' && <TestTimeout key={`test-timeout`} />}
      {state.screen.type === 'STEP' && (
        <Step key={`test-step-${state.screen.stepId}`} />
      )}
      <ExitGuard />
    </>
  );
}
